import { WebSocketError, SDKError, withErrorData } from "../types/error.types";

export interface WebSocketConfig {
  url: string;
  subscribeId: string;
  deviceFingerprint?: string;
  reconnectAttempts?: number;
  startTimeout?: number;
  isCaptionStream?: boolean;
  onError?: (error: SDKError) => void;
  onMessage?: (data: ArrayBuffer | any) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

export interface WebSocketMessage {
  subscribe?: {
    id: string;
  };
  unsubscribe?: {
    id: string;
  };
  uuid?: string;
}

/**
 * A WebSocket class that handles connection and message handling
 */
export class WebSocketClient {
  private socket: WebSocket | null = null;
  private config: WebSocketConfig;
  private reconnectCount: number = 0;
  private reconnectTimer: number | null = null;
  private closedManually: boolean = true;

  constructor(config: WebSocketConfig) {
    this.config = {
      reconnectAttempts: 3,
      startTimeout: 60000, // 1 minute
      ...config,
    };
  }

  /**
   * Connects to the WebSocket server and automatically subscribes
   */
  connect(): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      return;
    }

    if (this.socket) {
      this.closedManually = true;
      this.socket.close(1000, "Manually closed");
      this.socket = null;
    }

    try {
      console.log(
        "[WebSocketClient] connecting",
        `url: ${this.config.url}`,
        `subscriptionId: ${this.config.subscribeId}`,
      );
      this.socket = new WebSocket(this.config.url);
      this.setupEventHandlers();
      this.closedManually = false;
    } catch (error) {
      const sdkError = withErrorData(WebSocketError.ConnectionFailed, {
        url: this.config.url,
        reason: error instanceof Error ? error.message : String(error),
      });
      this.fail(sdkError);
    }
  }

  /**
   * Disconnects from the WebSocket server and automatically unsubscribes
   */
  disconnect(): void {
    this.closedManually = true;
    if (this.socket) {
      this.unsubscribe();
      this.socket.close(1000, "Manually closed");
      this.socket = null;
    }
    this.clearTimers();
  }

  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.onopen = () => {
      console.log(
        "[WebSocketClient] connection succeeded",
        `url: ${this.config.url}`,
        `subscriptionId: ${this.config.subscribeId}`,
      );
      this.reconnectCount = 0;
      this.subscribe();
      this.config.onOpen?.();
    };

    this.socket.onclose = (event) => {
      this.handleClose(event);
    };

    this.socket.onmessage = (event) => {
      if (event.data instanceof Blob) {
        event.data
          .arrayBuffer()
          .then((arrayBuffer) => {
            this.config.onMessage?.(arrayBuffer);
          })
          .catch((error: unknown) => {
            const sdkError = withErrorData(
              WebSocketError.BlobConversionFailed,
              {
                originalError:
                  error instanceof Error ? error.message : String(error),
              },
            );
            this.fail(sdkError);
          });
      } else {
        const sdkError = withErrorData(WebSocketError.BlobConversionFailed, {
          messageType: typeof event.data,
        });
        this.fail(sdkError);
      }
    };
  }

  private handleClose(event: CloseEvent): void {
    if (event.code === 1000 || this.closedManually) {
      return;
    }

    const reconnectAttempts = this.config.reconnectAttempts ?? 3;
    if (this.reconnectCount < reconnectAttempts) {
      this.handleReconnect();
    } else {
      // Exceeded reconnection attempts
      this.reconnectCount = 0;
      const sdkError = withErrorData(WebSocketError.ConnectionFailed, {
        url: this.config.url,
        attempts: reconnectAttempts,
      });
      this.fail(sdkError);
    }
  }

  private handleReconnect(): void {
    console.log(
      "[WebSocketClient] reconnecting",
      `url: ${this.config.url}`,
      `subscriptionId: ${this.config.subscribeId}`,
      `reconnectCount: ${this.reconnectCount + 1}`,
    );
    this.reconnectCount++;
    const delay = 1000 * Math.pow(2, this.reconnectCount); // Exponential backoff
    this.reconnectTimer = window.setTimeout(() => {
      this.connect();
    }, delay);
  }

  private clearTimers(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private sendMessage(message: any): void {
    if (this.closedManually) {
      return;
    }

    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      try {
        this.socket.send(JSON.stringify(message));
      } catch (error) {
        console.error(
          "WebSocketClient.sendMessage - Error sending message",
          error,
        );
      }
    } else {
      // TODO: retry connect
      const sdkError = withErrorData(WebSocketError.InvalidWebSocketState, {
        currentState: this.socket?.readyState || "null",
        operation: "sendMessage",
        message,
      });
      console.error(
        "WebSocketClient.sendMessage - Error sending message",
        JSON.stringify(sdkError, null, 2),
      );
      this.fail(sdkError);
    }
  }

  private subscribe(): void {
    try {
      const subscribeMessage: WebSocketMessage = {
        subscribe: {
          id: this.config.subscribeId,
        },
      };

      if (this.config.deviceFingerprint) {
        subscribeMessage.uuid = this.config.deviceFingerprint;
      }

      this.sendMessage(subscribeMessage);
    } catch (error) {
      const sdkError = withErrorData(WebSocketError.SubscriptionFailed, {
        subscribeId: this.config.subscribeId,
        originalError: error instanceof Error ? error.message : String(error),
      });
      this.config.onError?.(sdkError);
    }
  }

  private unsubscribe(): void {
    try {
      const unsubscribeMessage: WebSocketMessage = {
        unsubscribe: {
          id: this.config.subscribeId,
        },
      };

      if (this.config.deviceFingerprint) {
        unsubscribeMessage.uuid = this.config.deviceFingerprint;
      }

      this.sendMessage(unsubscribeMessage);
    } catch (error) {
      console.error("Error unsubscribing from WebSocket", error);
    }
  }

  private fail(error: SDKError): void {
    this.socket = null;
    this.config.onError?.(error);
  }

  /**
   * Checks if the WebSocket is currently connected
   */
  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }
}
